import { Box, Center, Spinner, Stack } from "@chakra-ui/react";
import { useParams } from "react-router";

import { useInvestmentInquiries } from "@equidefi/portals/hooks/useInquiries";
import { useGetInvestment } from "@equidefi/portals/hooks/useInvestments";
import { INQUIRY_TYPE } from "@equidefi/shared";
import type { DTO } from "@equidefi/shared/types";
import { Heading, Text } from "@equidefi/ui";

import { VAULT_ROUTES } from "@/constants/routes";
import { VaultContainer } from "../../../components";
import { InquiryCard, InvestmentTitleCard, VaultPageHeader } from "../../components";

const InquiryListPage = () => {
  const { id: investmentId } = useParams<{ id: string }>();

  const { data: inquiriesData, isFetched } = useInvestmentInquiries(investmentId as string);
  const { data: investment, isFetched: isInvestmentFetched } = useGetInvestment(investmentId as string);

  if (!isFetched || !isInvestmentFetched) {
    return (
      <Center h="100vh">
        <Spinner />
      </Center>
    );
  }

  const inquiries: DTO.TInquiry[] = inquiriesData?.data ?? [];
  const seenKyc = new Set<string>();
  const kycActive = inquiries.filter(
    (i) => i.type === INQUIRY_TYPE.KYC && !!i.user_id && !seenKyc.has(i.user_id) && (seenKyc.add(i.user_id), true),
  );
  const kyb = inquiries.find((i) => i.type === INQUIRY_TYPE.KYB);

  const issuer = investment?.issuer;
  const offering = investment?.offering;

  return (
    <VaultContainer pb="10" maxW={{ base: "560px", lg: "1040px" }}>
      <VaultPageHeader
        crumbs={[
          { label: "Portfolio", to: "/vault/dashboard" },
          { label: issuer?.name ?? "Issuer", to: VAULT_ROUTES.offering(offering?.id ?? "") },
          { label: offering?.name ?? "Offering", to: VAULT_ROUTES.offering(offering?.id ?? "") },
          { label: "Investment", to: VAULT_ROUTES.investmentDetails(investmentId as string) },
          { label: "Identity Verification" },
        ]}
        backTo={VAULT_ROUTES.investmentDetails(investmentId as string)}
        backLabel="Back To Investment"
      />

      <Stack spacing="4">
        <InvestmentTitleCard investment={investment} issuer={issuer} offering={offering} />
        <Box bg="white" borderRadius="md" boxShadow="md" p="4">
          <Heading fontWeight="bold" as="h1" mb="2">
            Identity Checks
          </Heading>
          <Text textStyle="body2" mb="4">
            Identity Checks (KYC / KYB) are required to participate in this offering.
          </Text>

          {kycActive.length === 0 ? (
            <Text color="equidefi.blue" mb="0">
              No Identity Checks have been started yet.
            </Text>
          ) : (
            <>
              <Text textStyle="body2" mb="3">
                Tap a card below to view more details.
              </Text>
              <Stack spacing="2.5">
                {kycActive.map((inquiry) => (
                  <InquiryCard
                    key={inquiry.id}
                    to={VAULT_ROUTES.inquiryKYCList(investmentId as string, inquiry.user_id)}
                    title={inquiry.display_name}
                    subtitle={
                      inquiry.user_id === investment?.user_id
                        ? "PRIMARY"
                        : investment?.vault?.account_type === "JOINT"
                          ? "SECONDARY"
                          : "UBO"
                    }
                    status={inquiry.status}
                  />
                ))}
              </Stack>
            </>
          )}
        </Box>

        {kyb && (
          <Box bg="white" borderRadius="md" boxShadow="md" p="4">
            <Heading fontWeight="bold" as="h2" mb="2">
              Entity Verification
            </Heading>
            <Text textStyle="body2" mb="4">
              Entity Verification (KYB) is required to participate in this offering as a trust or entity.
            </Text>
            <Text textStyle="body2" mb="3">
              Tap the card below to view more details.
            </Text>
            <InquiryCard
              to={VAULT_ROUTES.inquiryKYBList(investmentId as string, kyb.user_id)}
              title={kyb.display_name}
              subtitle="ENTITY"
              status={kyb.status}
            />
          </Box>
        )}
      </Stack>
    </VaultContainer>
  );
};

export default InquiryListPage;
